import { Money, Currency } from 'rpc/model/squareup/connect/v2/common/money';
import { Payment } from 'routes/profile/models/Payment';
import { dayjs } from 'utils/date';

/**
 * Pure aggregation helpers that turn the `Payment[]` returned by
 * `useListOrdersQuery` into the shapes the insight charts render. Kept free of
 * React so they can be unit tested in isolation.
 */

export interface SummaryStats {
  orderCount: number;
  itemCount: number;
  merchantCount: number;
  totalSpent: Money;
  averageOrder: Money;
  firstPurchase?: string;
  lastPurchase?: string;
}

export interface BarDatum {
  /** Unique key + primary label. */
  label: string;
  /** Aggregated money amount, in the smallest currency denomination. */
  amount: number;
  /** Optional secondary line (e.g. "12 orders"). */
  sublabel?: string;
  /** Optional merchant logo. */
  imageUrl?: string;
}

export interface MonthlyDatum {
  /** `YYYY-MM` key. */
  month: string;
  /** Human label, e.g. "Jan 2026". */
  label: string;
  amount: number;
  orderCount: number;
}

export interface Zip3Datum {
  /** First 3 digits of the merchant ZIP code (a ~metro-sized area). */
  zip3: string;
  amount: number;
  orderCount: number;
}

const amountOf = (money?: Money): number => money?.amount || 0;

/**
 * Turns a raw `businessType` token like `clothing_and_accessories` into a
 * sentence-cased label ("Clothing and accessories") for display.
 */
export function humanizeBusinessType(businessType: string): string {
  const words = businessType
    .trim()
    .toLowerCase()
    .split(/[\s_]+/)
    .filter(Boolean);
  if (words.length === 0) {
    return '';
  }
  return words
    .map((word, index) =>
      index === 0 ? word.charAt(0).toUpperCase() + word.slice(1) : word
    )
    .join(' ');
}

/**
 * Picks the currency the buyer transacts in most often so every total renders
 * consistently. Falls back to USD when no purchase carries a currency.
 */
export function dominantCurrency(payments: Payment[]): Currency {
  const counts = new Map<Currency, number>();
  payments.forEach((p) => {
    const currency = p.totalMoney?.currency;
    if (currency !== undefined) {
      counts.set(currency, (counts.get(currency) || 0) + 1);
    }
  });

  let best: Currency = Currency.USD;
  let bestCount = -1;
  counts.forEach((count, currency) => {
    if (count > bestCount) {
      best = currency;
      bestCount = count;
    }
  });
  return best;
}

export function summarize(payments: Payment[]): SummaryStats {
  const currency = dominantCurrency(payments);
  const merchants = new Set<string>();
  let totalSpent = 0;
  let itemCount = 0;
  let first: string | undefined;
  let last: string | undefined;

  payments.forEach((payment) => {
    totalSpent += amountOf(payment.totalMoney);
    itemCount += payment.totalItems;
    if (payment.merchantInfo.displayName) {
      merchants.add(payment.merchantInfo.displayName);
    }
    if (payment.createdAt) {
      if (!first || dayjs(payment.createdAt).isBefore(first)) {
        first = payment.createdAt;
      }
      if (!last || dayjs(payment.createdAt).isAfter(last)) {
        last = payment.createdAt;
      }
    }
  });

  return {
    averageOrder: Money.create({
      amount:
        payments.length > 0 ? Math.round(totalSpent / payments.length) : 0,
      currency,
    }),
    firstPurchase: first,
    itemCount,
    lastPurchase: last,
    merchantCount: merchants.size,
    orderCount: payments.length,
    totalSpent: Money.create({ amount: totalSpent, currency }),
  };
}

/** Spend grouped by merchant, richest first. */
export function spendByMerchant(payments: Payment[], limit = 8): BarDatum[] {
  const byMerchant: Record<
    string,
    { amount: number; orders: number; imageUrl?: string }
  > = {};

  payments.forEach((payment) => {
    const name = payment.merchantInfo.displayName || 'Unknown merchant';
    const entry = byMerchant[name] || { amount: 0, orders: 0 };
    entry.amount += amountOf(payment.totalMoney);
    entry.orders += 1;
    entry.imageUrl = entry.imageUrl || payment.merchantInfo.imageUrl;
    byMerchant[name] = entry;
  });

  return Object.entries(byMerchant)
    .map(([label, { amount, orders, imageUrl }]) => ({
      amount,
      imageUrl,
      label,
      sublabel: `${orders} ${orders === 1 ? 'order' : 'orders'}`,
    }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, limit);
}

/** Spend grouped by the merchant's business type / category, richest first. */
export function spendByCategory(payments: Payment[], limit = 8): BarDatum[] {
  const byCategory: Record<string, { amount: number; orders: number }> = {};

  payments.forEach((payment) => {
    const category = payment.merchantInfo.businessType?.trim();
    if (!category) {
      return;
    }
    const entry = byCategory[category] || { amount: 0, orders: 0 };
    entry.amount += amountOf(payment.totalMoney);
    entry.orders += 1;
    byCategory[category] = entry;
  });

  return Object.entries(byCategory)
    .map(([category, { amount, orders }]) => ({
      amount,
      label: humanizeBusinessType(category),
      sublabel: `${orders} ${orders === 1 ? 'order' : 'orders'}`,
    }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, limit);
}

/** Spend grouped by line-item name, richest first. */
export function spendByItem(payments: Payment[], limit = 8): BarDatum[] {
  const byItem: Record<string, { amount: number; count: number }> = {};

  payments.forEach((payment) => {
    payment.receipt.items.forEach((item) => {
      const name = item.name || 'Unnamed item';
      const entry = byItem[name] || { amount: 0, count: 0 };
      entry.amount += amountOf(item.amount);
      const quantity = Number.parseFloat(item.quantity);
      entry.count += Number.isFinite(quantity) ? quantity : 1;
      byItem[name] = entry;
    });
  });

  return Object.entries(byItem)
    .map(([label, { amount, count }]) => ({
      amount,
      label,
      sublabel: `${count % 1 === 0 ? count : count.toFixed(2)} purchased`,
    }))
    .sort((a, b) => b.amount - a.amount)
    .slice(0, limit);
}

/**
 * Monthly spend across the full range between the first and last purchase, with
 * empty months zero-filled so the timeline reads continuously.
 */
export function spendOverTime(payments: Payment[]): MonthlyDatum[] {
  const dated = payments.filter((p) => p.createdAt);
  if (dated.length === 0) {
    return [];
  }

  const byMonth = new Map<string, { amount: number; orderCount: number }>();
  let min = dayjs(dated[0].createdAt);
  let max = min;

  dated.forEach((payment) => {
    const date = dayjs(payment.createdAt);
    if (date.isBefore(min)) {
      min = date;
    }
    if (date.isAfter(max)) {
      max = date;
    }
    const key = date.format('YYYY-MM');
    const entry = byMonth.get(key) || { amount: 0, orderCount: 0 };
    entry.amount += amountOf(payment.totalMoney);
    entry.orderCount += 1;
    byMonth.set(key, entry);
  });

  const result: MonthlyDatum[] = [];
  let cursor = min.startOf('month');
  const end = max.startOf('month');
  // Cap the timeline so an unusually old first purchase can't explode the axis.
  let guard = 0;
  while (!cursor.isAfter(end) && guard < 240) {
    const key = cursor.format('YYYY-MM');
    const entry = byMonth.get(key) || { amount: 0, orderCount: 0 };
    result.push({
      amount: entry.amount,
      label: cursor.format('MMM YYYY'),
      month: key,
      orderCount: entry.orderCount,
    });
    cursor = cursor.add(1, 'month');
    guard += 1;
  }
  return result;
}

/**
 * The 3-digit ZIP prefix of the merchant's location (where the buyer shopped),
 * or `undefined` when the address has no usable ZIP. A ZIP3 covers a roughly
 * metro-sized area, so it's the granularity the bubble map plots.
 */
function merchantZip3(payment: Payment): string | undefined {
  const digits = payment.merchantInfo.address?.postalCode?.replace(/\D/g, '');
  return digits && digits.length >= 3 ? digits.slice(0, 3) : undefined;
}

/**
 * Spend grouped by the merchant location's 3-digit ZIP prefix, based on where
 * the buyer shopped rather than a shipping/billing destination. The map places
 * each group at its ZIP3 centroid; groups whose ZIP3 has no known centroid are
 * simply not plotted.
 */
export function spendByZip3(payments: Payment[]): Record<string, Zip3Datum> {
  const byZip3: Record<string, Zip3Datum> = {};

  payments.forEach((payment) => {
    const zip3 = merchantZip3(payment);
    if (!zip3) {
      return;
    }

    const entry = byZip3[zip3] || { amount: 0, orderCount: 0, zip3 };
    entry.amount += amountOf(payment.totalMoney);
    entry.orderCount += 1;
    byZip3[zip3] = entry;
  });

  return byZip3;
}
